import { ArrowUpRight } from 'lucide-react'
import { CellPreviewCard } from '@renderer/overlay/Sidepanel/components/cell-preview-card'
import { formatTimeAgo } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { BriefLinkIcon } from './link-pill'
import type { BriefLink, BriefLinkPreview } from '../types'

const SOURCE_LABELS: Record<string, string> = {
  'audio-note': 'Meeting note',
  audionote: 'Meeting note',
  drive: 'Google Drive',
  gmail: 'Gmail',
  google_docs: 'Google Docs',
  google_drive: 'Google Drive',
  linear: 'Linear',
  notion: 'Notion',
  slack: 'Slack',
  web: 'Web',
}

/** Provenance sources are raw backend strings; title-case whatever isn't named above. */
function sourceLabel(source: string, url: string): string {
  const key = source.trim().toLowerCase()
  const known = SOURCE_LABELS[key]
  if (known) return known
  if (!key) return hostname(url)
  return key
    .split(/[_\-\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return 'Link'
  }
}

/** `contentTime` is whatever the record carried: a timestamp when we can parse one, its own words otherwise. */
function formatContentTime(contentTime: string): string {
  const parsed = Date.parse(contentTime)
  return Number.isNaN(parsed) ? contentTime : formatTimeAgo(new Date(parsed))
}

interface BriefLinkPreviewCardProps {
  link: BriefLink
  preview: BriefLinkPreview
}

/**
 * Hover card for a relevant link: where the record came from, when it was
 * written, and the `support` lines the brief wrote to justify citing it.
 *
 * Reads the v2 structured brief rather than fetching the record: the pills are
 * links out to Slack/Notion/Gmail, which the app cannot preview, and the
 * citation is the part the user is actually deciding on.
 */
export function BriefLinkPreviewCard({ link, preview }: BriefLinkPreviewCardProps) {
  const open = () => window.open(link.url, '_blank', 'noopener,noreferrer')
  const source = preview.source || link.source
  const label = sourceLabel(source, link.url)
  const time = preview.contentTime ? formatContentTime(preview.contentTime) : null
  // undefined, not an empty node: the shell drops its body slot (and the gap
  // above it) only when there are no children at all.
  const support =
    preview.support.length > 0 ? (
      <div className="flex flex-col gap-1">
        {preview.support.map((line) => (
          <p key={line} className="text-foreground-secondary type-prose-sm line-clamp-3">
            {line}
          </p>
        ))}
      </div>
    ) : undefined

  return (
    <CellPreviewCard.Shell
      icon={<BriefLinkIcon url={link.url} source={source} size={16} />}
      title={preview.title?.trim() || link.title}
      source={
        <span className="type-ui-xs flex min-w-0 items-center">
          <span className="text-foreground-secondary min-w-0 truncate font-medium">{label}</span>
          {time && <span className="text-foreground-placeholder shrink-0"> · {time}</span>}
        </span>
      }
      onOpen={open}
      actions={
        <CellPreviewCard.PillButton onClick={open}>
          Open
          <ArrowUpRight aria-hidden="true" />
        </CellPreviewCard.PillButton>
      }
    >
      {support}
    </CellPreviewCard.Shell>
  )
}
